// client/src/dnd/Sidebar_parts/character_parts/CharacterUploadForm.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import CharacterSheetPreview from "./CharacterSheetPreview";

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v = v / 1024;
    i += 1;
  }
  const s = i === 0 ? String(Math.round(v)) : String(Math.round(v * 10) / 10);
  return `${s} ${units[i]}`;
}

function isLikelyFoundryActor(obj) {
  if (!obj || typeof obj !== "object") return false;
  if (typeof obj.name !== "string" || !obj.name) return false;
  if (obj.system && typeof obj.system === "object") return true;
  if (obj.data && typeof obj.data === "object") return true;
  if (obj.items && Array.isArray(obj.items)) return true;
  return false;
}

export default function CharacterUploadForm(props) {
  const {
    title = "Upload character",
    onSave = () => {},
    onCancel = () => {},
  } = props;

  const fileRef = useRef(null);

  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);

  const accept = useMemo(() => ".json,application/json,.pdf,application/pdf,.txt,text/plain", []);

  async function readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = () => reject(new Error("Failed to read file"));
      r.readAsText(file);
    });
  }

  function openPicker() {
    if (busy) return;
    if (fileRef.current) fileRef.current.click();
  }

  async function buildPreview(file) {
    setErr("");
    if (!file) return;

    if (preview && preview.objectUrl && typeof URL !== "undefined") {
      try {
        URL.revokeObjectURL(preview.objectUrl);
      } catch {}
    }

    setPreview(null);
    setBusy(true);
    try {
      const name = String(file.name || "upload");
      const type = String(file.type || "");
      const size = Number(file.size || 0);

      const lower = name.toLowerCase();
      const isJson = type === "application/json" || lower.endsWith(".json");
      const isTxt = type === "text/plain" || lower.endsWith(".txt");
      const isPdf = type === "application/pdf" || lower.endsWith(".pdf");

      const objectUrl = typeof URL !== "undefined" ? URL.createObjectURL(file) : null;

      let rawText = "";
      let parsed = null;

      if (isJson || isTxt) {
        rawText = await readFileAsText(file);
        if (isJson) parsed = safeJsonParse(rawText);
      }

      let detected = "file";
      if (isPdf) detected = "pdf";
      else if (isTxt) detected = "text";
      else if (parsed && isLikelyFoundryActor(parsed)) detected = "foundry";
      else if (parsed && isJson) detected = "generic";
      else if (isJson && !parsed) detected = "bad_json";

      setPreview({
        detected,
        file: { name, type, size, size_human: formatBytes(size) },
        objectUrl,
        raw: isJson ? { type: "json-text", text: rawText } : isTxt ? { type: "text", text: rawText } : { type: "binary" },
        parsed: parsed || null,
        createdAt: new Date().toISOString(),
      });
    } catch (e) {
      setErr(String(e && e.message ? e.message : e));
    } finally {
      setBusy(false);
    }
  }

  function onFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    buildPreview(file);
    e.target.value = "";
  }

  function onDrop(e) {
    e.preventDefault();
    if (busy) return;
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    buildPreview(file);
  }

  function onDragOver(e) {
    e.preventDefault();
    if (busy) return;
    setDragOver(true);
  }

  function onDragLeave(e) {
    e.preventDefault();
    if (busy) return;
    setDragOver(false);
  }

  function reset() {
    setErr("");
    if (preview && preview.objectUrl && typeof URL !== "undefined") {
      try {
        URL.revokeObjectURL(preview.objectUrl);
      } catch {}
    }
    setPreview(null);
  }

  async function handleSave() {
    if (!preview) return;
    if (preview.detected === "bad_json") {
      setErr("JSON file could not be parsed. Fix the file and try again.");
      return;
    }

    setErr("");
    setBusy(true);
    try {
      const r = await onSave(preview);
      if (r === false) {
        setErr("Save failed.");
      }
    } catch (e) {
      setErr(String(e && e.message ? e.message : e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    return () => {
      if (preview && preview.objectUrl && typeof URL !== "undefined") {
        try {
          URL.revokeObjectURL(preview.objectUrl);
        } catch {}
      }
    };
  }, [preview]);

  const button = (kind) => ({
    appearance: "none",
    border: "1px solid " + (kind === "primary" ? "#214b2d" : "#333"),
    background: kind === "primary" ? "#0e1b12" : "#0c0c0c",
    color: kind === "primary" ? "#c9ffd7" : "#eee",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    cursor: busy ? "not-allowed" : "pointer",
    opacity: busy ? 0.8 : 1,
  });

  return (
    <div
      style={{
        border: "1px solid #333",
        borderRadius: 14,
        padding: 14,
        background: "#111",
        color: "#eee",
        width: "min(1100px, calc(100vw - 24px))",
        maxHeight: "min(90vh, 900px)",
        overflow: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 900, flex: 1 }}>{title}</div>
        <button onClick={onCancel} disabled={busy} style={button("secondary")}>Close</button>
      </div>

      {!preview ? (
        <>
          <div
            onClick={openPicker}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            style={{
              marginTop: 12,
              border: `2px dashed ${dragOver ? "#66a" : "#333"}`,
              borderRadius: 14,
              padding: 18,
              background: dragOver ? "#101425" : "#0b0b0b",
              cursor: busy ? "not-allowed" : "pointer",
              userSelect: "none",
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>
              {busy ? "Reading…" : "Drop a file here, or click to choose"}
            </div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              After selecting a file, you’ll see a sheet-style preview (not JSON).
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept={accept}
            onChange={onFileChange}
            style={{ display: "none" }}
            disabled={busy}
          />

          {err ? (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 12,
                background: "#2a0f12",
                border: "1px solid #5a1f22",
                color: "#ffd6d9",
                whiteSpace: "pre-wrap",
              }}
            >
              {err}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 12,
              background: "#0b0b0b",
              border: "1px solid #222",
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontWeight: 900, flex: 1, minWidth: 240 }}>
              {preview.parsed && preview.parsed.name ? preview.parsed.name : preview.file.name} · detected: {preview.detected}
            </div>

            {preview.objectUrl ? (
              <a
                href={preview.objectUrl}
                target="_blank"
                rel="noreferrer"
                style={{ color: "#9ad", fontWeight: 900, textDecoration: "none" }}
              >
                Open file locally
              </a>
            ) : null}

            <button onClick={reset} disabled={busy} style={button("secondary")}>
              Import different file
            </button>
            <button onClick={handleSave} disabled={busy} style={button("primary")}>
              {busy ? "Saving…" : "Save"}
            </button>

            <div style={{ width: "100%", fontSize: 12, opacity: 0.8 }}>
              type: {preview.file.type || "unknown"} · size: {preview.file.size_human} · createdAt: {preview.createdAt}
            </div>

            {preview.detected === "bad_json" ? (
              <div style={{ width: "100%", fontSize: 12, color: "#ffd6d9", fontWeight: 900 }}>
                JSON file could not be parsed.
              </div>
            ) : null}

            {err ? (
              <div
                style={{
                  width: "100%",
                  marginTop: 8,
                  padding: 10,
                  borderRadius: 12,
                  background: "#2a0f12",
                  border: "1px solid #5a1f22",
                  color: "#ffd6d9",
                  whiteSpace: "pre-wrap",
                }}
              >
                {err}
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: 12 }}>
            <CharacterSheetPreview preview={preview} />
          </div>
        </>
      )}
    </div>
  );
}
